import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Download, Upload, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/content-sync")({
  component: ContentSyncPage,
});

type TableName =
  | "pages" | "posts" | "categories" | "faqs" | "testimonials"
  | "products" | "product_variants" | "product_reviews"
  | "kb_categories" | "kb_articles";

type Group = { id: string; label: string; tables: { name: TableName; label: string }[] };

const GROUPS: Group[] = [
  {
    id: "marketing",
    label: "Marketing content",
    tables: [
      { name: "pages", label: "Pages" },
      { name: "posts", label: "Blog posts" },
      { name: "faqs", label: "FAQs" },
      { name: "testimonials", label: "Testimonials" },
    ],
  },
  {
    id: "catalog",
    label: "Catalog",
    tables: [
      { name: "categories", label: "Categories" },
      { name: "products", label: "Products" },
      { name: "product_variants", label: "Product variants" },
      { name: "product_reviews", label: "Product reviews" },
    ],
  },
  {
    id: "support",
    label: "Support content",
    tables: [
      { name: "kb_categories", label: "KB categories" },
      { name: "kb_articles", label: "KB articles" },
      { name: "faqs", label: "FAQs" },
    ],
  },
];

const ALL_TABLES: { name: TableName; label: string }[] = Array.from(
  new Map(GROUPS.flatMap((g) => g.tables).map((t) => [t.name, t])).values(),
);

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ContentSyncPage() {
  const [tab, setTab] = useState<string>("everything");
  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Content Sync"
        description="Export your content to JSON for backups or moving between projects. Import to restore or seed."
      />

      <Card className="border-amber-500/30 bg-amber-500/5 p-4">
        <div className="flex gap-3 text-sm">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" />
          <div>
            <p className="font-medium">Heads up</p>
            <p className="text-muted-foreground">
              Import upserts rows by <code className="rounded bg-muted px-1">id</code>. Existing rows with matching IDs will be overwritten.
              Always export a backup before importing.
            </p>
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <PillTabsList>
          <PillTabsTrigger value="everything">Everything</PillTabsTrigger>
          <PillTabsTrigger value="marketing">Marketing</PillTabsTrigger>
          <PillTabsTrigger value="catalog">Catalog</PillTabsTrigger>
          <PillTabsTrigger value="support">Support</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="everything" className="pt-4">
          <SyncPanel tables={ALL_TABLES} groupLabel="Everything" />
        </TabsContent>
        {GROUPS.map((g) => (
          <TabsContent key={g.id} value={g.id} className="pt-4">
            <SyncPanel tables={g.tables} groupLabel={g.label} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function SyncPanel({ tables, groupLabel }: { tables: { name: TableName; label: string }[]; groupLabel: string }) {
  const [selected, setSelected] = useState<Set<string>>(new Set(tables.map((t) => t.name)));
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [busy, setBusy] = useState<"idle" | "export" | "import" | "count">("idle");
  const fileRef = useRef<HTMLInputElement>(null);

  const toggle = (name: string) => {
    const next = new Set(selected);
    next.has(name) ? next.delete(name) : next.add(name);
    setSelected(next);
  };

  const refreshCounts = async () => {
    setBusy("count");
    const result: Record<string, number | null> = {};
    for (const t of tables) {
      const { count, error } = await (supabase.from(t.name) as any)
        .select("*", { count: "exact", head: true });
      result[t.name] = error ? null : count ?? 0;
    }
    setCounts(result);
    setBusy("idle");
  };

  const handleExport = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one content type");
      return;
    }
    setBusy("export");
    try {
      const bundle: Record<string, any[]> = {};
      for (const t of tables) {
        if (!selected.has(t.name)) continue;
        const rows: any[] = [];
        let from = 0;
        const PAGE = 1000;
        // paginate to bypass 1000-row default limit
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { data, error } = await (supabase.from(t.name) as any)
            .select("*")
            .range(from, from + PAGE - 1);
          if (error) throw error;
          rows.push(...(data ?? []));
          if (!data || data.length < PAGE) break;
          from += PAGE;
        }
        bundle[t.name] = rows;
      }
      const payload = {
        _meta: {
          exported_at: new Date().toISOString(),
          group: groupLabel,
          version: 1,
          tables: Object.keys(bundle),
        },
        data: bundle,
      };
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      downloadJson(`content-sync-${groupLabel.toLowerCase().replace(/\s+/g, "-")}-${stamp}.json`, payload);
      toast.success(`Exported ${Object.values(bundle).reduce((a, b) => a + b.length, 0)} rows`);
    } catch (e: any) {
      toast.error(e.message ?? "Export failed");
    } finally {
      setBusy("idle");
    }
  };

  const handleImportFile = async (file: File) => {
    setBusy("import");
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const data: Record<string, any[]> = parsed?.data ?? parsed;
      if (!data || typeof data !== "object") throw new Error("Invalid file format");

      let totalInserted = 0;
      const errors: string[] = [];
      const importOrder: TableName[] = [
        "categories", "kb_categories", "products", "product_variants",
        "pages", "posts", "faqs", "testimonials", "product_reviews", "kb_articles",
      ];

      for (const t of importOrder) {
        if (!selected.has(t)) continue;
        const rows = data[t];
        if (!Array.isArray(rows) || rows.length === 0) continue;

        const CHUNK = 200;
        for (let i = 0; i < rows.length; i += CHUNK) {
          const chunk = rows.slice(i, i + CHUNK);
          const { error } = await (supabase.from(t) as any).upsert(chunk, { onConflict: "id" });
          if (error) {
            errors.push(`${t}: ${error.message}`);
            break;
          }
          totalInserted += chunk.length;
        }
      }

      if (errors.length) {
        toast.error(`Imported ${totalInserted} rows with errors`, { description: errors.join("\n") });
      } else {
        toast.success(`Imported ${totalInserted} rows`);
      }
      await refreshCounts();
    } catch (e: any) {
      toast.error(e.message ?? "Import failed");
    } finally {
      setBusy("idle");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <Label className="text-sm">Content types in "{groupLabel}"</Label>
          <Button variant="ghost" size="sm" onClick={refreshCounts} disabled={busy !== "idle"}>
            {busy === "count" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            Refresh counts
          </Button>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
          {tables.map((t) => (
            <label
              key={t.name}
              className="flex cursor-pointer items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm hover:bg-accent/50"
            >
              <div className="flex items-center gap-2">
                <Checkbox checked={selected.has(t.name)} onCheckedChange={() => toggle(t.name)} />
                <span>{t.label}</span>
              </div>
              {counts[t.name] != null && (
                <Badge variant="secondary" className="text-xs">{counts[t.name]} rows</Badge>
              )}
            </label>
          ))}
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-4">
          <h3 className="mb-1 font-medium">Export</h3>
          <p className="mb-3 text-sm text-muted-foreground">
            Downloads a JSON file with all selected content. Safe to keep as backup.
          </p>
          <Button onClick={handleExport} disabled={busy !== "idle"}>
            {busy === "export" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export selected
          </Button>
        </Card>

        <Card className="p-4">
          <h3 className="mb-1 font-medium">Import</h3>
          <p className="mb-3 text-sm text-muted-foreground">
            Upload a JSON file previously exported. Rows are upserted by ID.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                if (confirm(`Import "${file.name}"? Existing rows with matching IDs will be overwritten.`)) {
                  handleImportFile(file);
                } else {
                  if (fileRef.current) fileRef.current.value = "";
                }
              }
            }}
          />
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={busy !== "idle"}
          >
            {busy === "import" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            Choose JSON file…
          </Button>
        </Card>
      </div>
    </div>
  );
}
